/**
 * Project-level testability contract frontmatter.
 *
 * Lives at `iamu/testability.md` as YAML frontmatter + markdown execution
 * boundaries body. Defines the safety fence and dev environment target
 * so autonomous walkers stay confined to local test surfaces.
 */
import { z } from "zod";
import { SCHEMA_VERSION } from "./version.js";

export const testabilityStatusEnum = z.enum(["draft", "verified"]);
export type TestabilityStatus = z.infer<typeof testabilityStatusEnum>;

export const authStrategyEnum = z.enum([
  "dev_switcher",
  "interactive",
  "test_accounts",
  "public",
]);
export type AuthStrategy = z.infer<typeof authStrategyEnum>;

export const testabilityFrontmatterSchema = z.object({
  schema_version: z.number().int().optional(),
  status: testabilityStatusEnum.default("draft"),
  target_url: z
    .string()
    .url()
    .describe("Target URL where the product is hosted or served locally."),
  dev_command: z
    .string()
    .max(1000)
    .optional()
    .describe("Dev script or startup command to boot the app locally."),
  auth_strategy: authStrategyEnum
    .or(z.string().min(1).max(100))
    .optional()
    .describe("How personas authenticate in test environments."),
  access: z
    .string()
    .max(4096)
    .optional()
    .describe(
      'How a walker becomes a given user in this product: the switching mechanism, written once for the project. Example: \'No login in dev. In the page, run localStorage.setItem("dev_persona", "<persona access>") and reload.\' Each persona\'s own `access` names which user they are. Never put passwords or tokens here — name where they live instead.',
    ),
  harnesses: z
    .array(z.string().min(1).max(500))
    .max(64)
    .optional()
    .describe("Mocking frameworks, seed scripts, or test harnesses present."),
  untestable_boundaries: z
    .array(z.string().min(1).max(1024))
    .max(128)
    .optional()
    .describe(
      "External services, third-party APIs, or surfaces forbidden to autonomous walkers.",
    ),
});
export type TestabilityFrontmatter = z.infer<
  typeof testabilityFrontmatterSchema
>;

export interface LocalTestability {
  frontmatter: TestabilityFrontmatter;
  body: string;
}

export function emptyTestability(
  targetUrl = "http://localhost:3000",
): LocalTestability {
  return {
    frontmatter: {
      schema_version: SCHEMA_VERSION,
      status: "draft",
      target_url: targetUrl,
    },
    body: `# Project Testability & Environment

Target URL and execution harness for walkthroughs.
Audited and populated during /iamu:init.

## Target & Run Instructions
- Default URL: ${targetUrl}

## Reachable Surfaces
(Run /iamu:init to discover testable routes)

## Mocks & Data Fixtures
(Run /iamu:init to inspect existing harnesses)

## Untestable Boundaries
(None documented yet)
`,
  };
}
